/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaPaperPlane,
} from "react-icons/fa";
import { motion } from "framer-motion";

const GetInTouch = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
  });
  const { t } = useTranslation();

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form data:", formData);
    setFormData({
      name: "",
      email: "",
      description: "",
    });
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 ">
            {t("contactUs.getInTouch.title")}
          </h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
            {t("contactUs.getInTouch.description")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl shadow-lg p-10 transform hover:scale-[1.01] transition-all duration-300"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t("contactUs.getInTouch.name")}
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="w-full border-2 border-gray-200 bg-white rounded-xl p-4 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition-all duration-300"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t("contactUs.getInTouch.name")}
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t("contactUs.getInTouch.email")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="w-full border-2 border-gray-200 bg-white rounded-xl p-4 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition-all duration-300"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("contactUs.getInTouch.email")}
                  />
                </div>
                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t("contactUs.getInTouch.details")}
                  </label>
                  <textarea
                    id="description"
                    className="w-full border-2 border-gray-200 bg-white rounded-xl p-4 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition-all duration-300 h-[12rem] resize-none"
                    required
                    value={formData.description}
                    onChange={handleChange}
                    placeholder={t("contactUs.getInTouch.details")}
                  />
                </div>
              </div>
              <button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xl  rounded-xl p-4 transition-all duration-300 ease-in-out flex items-center justify-center space-x-2 group"
                type="submit"
              >
                <span>{t("contactUs.getInTouch.ctaText")}</span>
                <FaPaperPlane className="transform group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="bg-white rounded-xl shadow-lg p-10">
              <div className="space-y-8">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-600/10 p-4 rounded-full">
                    <FaEnvelope className="text-blue-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">
                      Email
                    </h3>
                    <p className="text-gray-600">info@sequenx.com</p>
                  </div>
                </div>
                {/* <div className="flex items-center space-x-4">
                  <div className="bg-blue-600/10 p-4 rounded-full">
                    <FaPhone className="text-blue-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">
                      Phone
                    </h3>
                    <p className="text-gray-600">+1 (555) 123-4567</p>
                  </div>
                </div> */}
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-600/10 p-4 rounded-full">
                    <FaMapMarkerAlt className="text-blue-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">
                      Location
                    </h3>
                    <p className="text-gray-600">
                      Mozartlaan 9, 2215LS, Voorhout, The Netherlands
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="font-semibold text-gray-800 text-lg mb-4">
                  Business Hours
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Monday - Friday</span>
                    <span>9:00 AM - 5:30 PM</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Saturday & Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default GetInTouch;
